/**
 * Collapsible "New model price" form for the back-office price book. Self-gates
 * on `device.write`. Brand/model are free text (the catalog is offered as
 * type-ahead suggestions, but operators can add a brand or coin a new model on
 * the spot — the API stores both as free text). Currency comes from
 * GET /api/country-config; money is captured in minor units (the API DTO unit).
 *
 * Lending price mode (ADR-0014):
 *   - fixed      → one flat financed total (>= value), any tenor.
 *   - per_period → cost + a per-month margin × tenor. The margin is either an
 *     absolute amount (per month) or a percent of cost (basis points / month).
 *   - per_tenor  → an explicit financed total per offered tenor (the ladder).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type {
  CreateModelPriceRequest,
  ModelPrice,
  ModelRiskClass,
  PerPeriodBasis,
} from "@dfp/api-client";
import { Button } from "./Button.js";
import { Field, FormError, Select, TextInput, apiErrorMessage } from "./Form.js";
import { PerTenorEditor, emptyTenorRow, type PerTenorRow } from "./PerTenorEditor.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";
import { CONTROL_PROVIDERS, DEVICE_CATALOG, modelsForBrand } from "../lib/device-catalog.js";
import { useCountryConfig } from "../lib/useCountryConfig.js";

const RISK_CLASSES: readonly ModelRiskClass[] = ["A", "B", "C"];
type Mode = "" | "per_period" | "fixed" | "per_tenor";

export function CreateModelPriceForm(): JSX.Element | null {
  const auth = useAuth();
  const canWrite = (auth.user?.permissions ?? []).includes("device.write");
  const qc = useQueryClient();

  const [open, setOpen] = useState(false);
  const cfg = useCountryConfig(open);
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [amountMinor, setAmountMinor] = useState("");
  const [downPaymentMinor, setDownPaymentMinor] = useState("");
  const [lendingPriceMode, setLendingPriceMode] = useState<Mode>("");
  const [lendingPriceMinor, setLendingPriceMinor] = useState("");
  const [perPeriodBasis, setPerPeriodBasis] = useState<PerPeriodBasis>("amount");
  const [perPeriodRateBps, setPerPeriodRateBps] = useState("");
  const [perTenor, setPerTenor] = useState<readonly PerTenorRow[]>([emptyTenorRow()]);
  const [modelRiskClass, setModelRiskClass] = useState<ModelRiskClass>("B");
  const [controlProvider, setControlProvider] = useState("");
  const [capabilities, setCapabilities] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: CreateModelPriceRequest) => api.post<ModelPrice>("/api/model-prices", body),
    onSuccess: () => {
      reset();
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ["model-prices", "list"] });
    },
    onError: (err: unknown) => setError(apiErrorMessage(err)),
  });

  function reset(): void {
    setBrand("");
    setModel("");
    setAmountMinor("");
    setDownPaymentMinor("");
    setLendingPriceMode("");
    setLendingPriceMinor("");
    setPerPeriodBasis("amount");
    setPerPeriodRateBps("");
    setPerTenor([emptyTenorRow()]);
    setModelRiskClass("B");
    setControlProvider("");
    setCapabilities("");
    setError(null);
  }

  function submit(): void {
    setError(null);
    if (brand.trim().length < 1 || model.trim().length < 1) {
      setError("Brand and model are required.");
      return;
    }
    const amount = Number(amountMinor);
    if (!Number.isInteger(amount) || amount <= 0) {
      setError("Value must be a positive whole number (minor units).");
      return;
    }
    const cur = cfg.data?.currency;
    if (!cur) {
      setError("Currency is still loading.");
      return;
    }
    const downPayment = Number(downPaymentMinor);
    if (!Number.isInteger(downPayment) || downPayment < 0 || downPayment > amount) {
      setError("Down payment must be a whole number not exceeding the value.");
      return;
    }
    if (
      lendingPriceMode !== "per_period" &&
      lendingPriceMode !== "fixed" &&
      lendingPriceMode !== "per_tenor"
    ) {
      setError("Select a lending price mode.");
      return;
    }

    const caps = capabilities
      .split(",")
      .map((c) => c.trim())
      .filter((c) => c.length > 0);
    const common = {
      brand: brand.trim(),
      model: model.trim(),
      value: { amountMinor: amount, currency: cur },
      downPaymentMinor: downPayment,
      modelRiskClass,
      controlProvider: controlProvider.trim(),
      capabilities: caps,
    };
    if (controlProvider.trim().length < 1) {
      setError("Control provider is required.");
      return;
    }

    if (lendingPriceMode === "fixed") {
      const lendingPrice = Number(lendingPriceMinor);
      if (!Number.isInteger(lendingPrice) || lendingPrice <= 0) {
        setError("Lending price must be a positive whole number (minor units).");
        return;
      }
      if (lendingPrice < amount) {
        setError("A fixed lending price must be at least the device value.");
        return;
      }
      mutation.mutate({ ...common, lendingPriceMode: "fixed", lendingPriceMinor: lendingPrice });
      return;
    }

    if (lendingPriceMode === "per_period") {
      if (perPeriodBasis === "percent") {
        const rate = Number(perPeriodRateBps);
        if (!Number.isInteger(rate) || rate <= 0) {
          setError("Per-month rate must be a positive whole number (basis points of cost).");
          return;
        }
        mutation.mutate({
          ...common,
          lendingPriceMode: "per_period",
          lendingPriceMinor: 0,
          perPeriodBasis: "percent",
          perPeriodRateBps: rate,
        });
        return;
      }
      const margin = Number(lendingPriceMinor);
      if (!Number.isInteger(margin) || margin <= 0) {
        setError("Per-month margin must be a positive whole number (minor units).");
        return;
      }
      mutation.mutate({
        ...common,
        lendingPriceMode: "per_period",
        lendingPriceMinor: margin,
        perPeriodBasis: "amount",
      });
      return;
    }

    // per_tenor
    const floor = amount - downPayment;
    const parsed: { tenorMonths: number; totalMinor: number }[] = [];
    const seen = new Set<number>();
    for (const row of perTenor) {
      if (row.tenorMonths.trim() === "" && row.total.trim() === "") continue; // skip blanks
      const t = Number(row.tenorMonths);
      const total = Number(row.total);
      if (!Number.isInteger(t) || t <= 0) {
        setError("Each tenor must be a positive whole number of months.");
        return;
      }
      if (seen.has(t)) {
        setError(`Duplicate tenor ${t} in the ladder.`);
        return;
      }
      seen.add(t);
      if (!Number.isInteger(total) || total < floor) {
        setError(`Tenor ${t} total must be a whole number ≥ value − down payment (${floor}).`);
        return;
      }
      parsed.push({ tenorMonths: t, totalMinor: total });
    }
    if (parsed.length === 0) {
      setError("Add at least one tenor row.");
      return;
    }
    mutation.mutate({
      ...common,
      lendingPriceMode: "per_tenor",
      lendingPriceMinor: 0,
      perTenor: parsed,
    });
  }

  if (!canWrite) return null;

  if (!open) {
    return <Button onClick={() => setOpen(true)}>New model price…</Button>;
  }

  const minorHint = `minor units${cfg.data ? `, ${cfg.data.currency}` : ""}`;

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3">
      <p className="text-sm font-medium text-slate-700">New model price</p>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Brand" hint="pick or type a new one">
          {/*
            Free text + a datalist of the curated catalog. Operators can
            choose an existing brand OR type a brand we don't stock yet —
            the API stores brand as free text, so this is the "add a brand"
            entry point.
          */}
          <TextInput
            list="brand-suggestions"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            placeholder="e.g. Samsung"
          />
          <datalist id="brand-suggestions">
            {DEVICE_CATALOG.map((b) => (
              <option key={b.brand} value={b.brand} />
            ))}
          </datalist>
        </Field>
        <Field label="Model" hint="pick or type a new one">
          <TextInput
            list="model-suggestions"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="e.g. Galaxy A06"
          />
          <datalist id="model-suggestions">
            {modelsForBrand(brand).map((m) => (
              <option key={m} value={m} />
            ))}
          </datalist>
        </Field>
        <Field label="Value" hint={minorHint}>
          <TextInput
            inputMode="numeric"
            value={amountMinor}
            onChange={(e) => setAmountMinor(e.target.value)}
          />
        </Field>
        <Field label="Down payment" hint="minor units">
          <TextInput
            inputMode="numeric"
            value={downPaymentMinor}
            onChange={(e) => setDownPaymentMinor(e.target.value)}
          />
        </Field>
        <Field label="Lending price mode">
          <Select
            value={lendingPriceMode}
            onChange={(e) => setLendingPriceMode(e.target.value as Mode)}
          >
            <option value="">Select mode…</option>
            <option value="fixed">Fixed (any tenor)</option>
            <option value="per_period">Per period (cost + margin × tenor)</option>
            <option value="per_tenor">Per tenor (explicit ladder)</option>
          </Select>
        </Field>

        {/* fixed: one flat total */}
        {lendingPriceMode === "fixed" ? (
          <Field label="Lending price" hint="financed total, minor units">
            <TextInput
              inputMode="numeric"
              value={lendingPriceMinor}
              onChange={(e) => setLendingPriceMinor(e.target.value)}
            />
          </Field>
        ) : null}

        {/* per_period: basis + the matching margin field */}
        {lendingPriceMode === "per_period" ? (
          <>
            <Field label="Margin basis" hint="per month">
              <Select
                value={perPeriodBasis}
                onChange={(e) => setPerPeriodBasis(e.target.value as PerPeriodBasis)}
              >
                <option value="amount">Amount (minor units / month)</option>
                <option value="percent">Percent of cost (per month)</option>
              </Select>
            </Field>
            {perPeriodBasis === "amount" ? (
              <Field label="Margin / month" hint={minorHint}>
                <TextInput
                  inputMode="numeric"
                  value={lendingPriceMinor}
                  onChange={(e) => setLendingPriceMinor(e.target.value)}
                />
              </Field>
            ) : (
              <Field label="Rate / month" hint="basis points of cost (100 = 1%)">
                <TextInput
                  inputMode="numeric"
                  value={perPeriodRateBps}
                  onChange={(e) => setPerPeriodRateBps(e.target.value)}
                />
              </Field>
            )}
          </>
        ) : null}

        <Field label="Model risk class">
          <Select
            value={modelRiskClass}
            onChange={(e) => setModelRiskClass(e.target.value as ModelRiskClass)}
          >
            {RISK_CLASSES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Control provider">
          <Select value={controlProvider} onChange={(e) => setControlProvider(e.target.value)}>
            <option value="">Select provider…</option>
            {CONTROL_PROVIDERS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {/* per_tenor: the ladder spans the full width below the grid */}
      {lendingPriceMode === "per_tenor" ? (
        <Field label="Tenor ladder" hint="one financed total per offered tenor">
          <div className="mt-1.5 rounded-xl border border-simuflex-line bg-white p-3">
            <PerTenorEditor
              rows={perTenor}
              onChange={setPerTenor}
              totalLabel="Total (minor units)"
            />
          </div>
        </Field>
      ) : null}

      <Field label="Capabilities" hint="comma-separated, optional">
        <TextInput
          value={capabilities}
          onChange={(e) => setCapabilities(e.target.value)}
          placeholder="lock, locate, wipe"
        />
      </Field>

      <FormError message={error} />

      <div className="flex gap-2">
        <Button onClick={submit} disabled={mutation.isPending}>
          {mutation.isPending ? "Creating…" : "Create model price"}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            reset();
            setOpen(false);
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
